import {
  postAimIntegrationVerify,
  postAimIntegrationVerifyById,
  postSnapshotAimIntegrationVerify,
} from '@ipfabric/api-client/client/model/aim/aim';
import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useDiscoverySettingsContext } from '../../../composition/DiscoverySettings/discoverySettingsContext';
import type { AimIntegrationDraft } from '../../../model/aimDrafts';
import {
  type AimIntegrationVerifyRequest,
  type ResolveAimIntegrationVerifyRequestArgs,
  resolveAimIntegrationVerifyRequest,
} from './aimIntegrationVerify';

const sendVerifyRequest = (request: AimIntegrationVerifyRequest) => {
  switch (request.kind) {
    case 'body':
      return postAimIntegrationVerify(request.body);
    case 'stored':
      return postAimIntegrationVerifyById(request.id, request.body);
    case 'storedInSnapshot':
      return postSnapshotAimIntegrationVerify(
        request.snapshotId,
        request.id,
        request.body,
      );
  }
};

type TestAimIntegrationConnectionHook = {
  isTesting: boolean;
  testConnection: (args: {
    values: ResolveAimIntegrationVerifyRequestArgs['values'];
    initialValues: AimIntegrationDraft | undefined;
  }) => Promise<void>;
};

/**
 * Run the AIM integration connection test for the integration form and report
 * the outcome as a toast.
 *
 * The endpoint is picked by `resolveAimIntegrationVerifyRequest`; the snapshot
 * scope comes from the surrounding settings form, so the same dialog works on
 * the global page and on both snapshot pages.
 */
export const useTestAimIntegrationConnection =
  (): TestAimIntegrationConnectionHook => {
    const { snapshotId } = useDiscoverySettingsContext();
    const notifications = useNotifications();
    const [testing, setTesting] = useLoadingState();

    const testConnection = useStableCallback(
      async ({
        values,
        initialValues,
      }: {
        values: ResolveAimIntegrationVerifyRequestArgs['values'];
        initialValues: AimIntegrationDraft | undefined;
      }) => {
        await withLoadingIndicator(setTesting)(async () => {
          try {
            await sendVerifyRequest(
              resolveAimIntegrationVerifyRequest({
                values,
                initialValues,
                snapshotId,
              }),
            );
            notifications.showNotification({
              message: 'Connection succeeded',
              type: 'success',
            });
          } catch (e) {
            notifications.showNotification({
              title: 'Connection failed',
              message:
                (isIpfError(e, 'API') && e?.payload?.response.message) ||
                'Unknown error',
              type: 'danger',
            });
          }
        });
      },
    );

    return { isTesting: testing === 'LOADING', testConnection };
  };
